/**
 * DashboardLauncher — post-login home page.
 *
 * Renders a permissions-aware card grid of dashboards the user can access.
 * Always shows the Chat card; gates other dashboards on their permission key
 * from frontend/src/config/dashboards.ts.
 */

import React from 'react';
import { User } from 'firebase/auth';
import Auth from '../Auth';
import GoogleConnect from '../GoogleConnect';
import { useVisibleDashboards } from '../../hooks/useVisibleDashboards';
import { UserOrganization } from '../../utils/authDomain';
import DashboardCard from './DashboardCard';

interface DashboardLauncherProps {
  user: User | null;
  authResolving?: boolean;
  userOrganization: UserOrganization;
  showSuccess: (notification: any) => void;
  showError: (notification: any) => void;
  showWarning: (notification: any) => void;
}

const formatToday = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const DashboardLauncher: React.FC<DashboardLauncherProps> = ({
  user,
  authResolving = false,
  userOrganization,
  showSuccess,
  showError,
  showWarning,
}) => {
  const { loading, dashboards: visible } = useVisibleDashboards();

  if (!user) {
    return <Auth user={user} authResolving={authResolving} />;
  }

  const firstName = (user.displayName || user.email || '').split(' ')[0]?.split('@')[0];

  return (
    <div className="App">
      <Auth
        user={user}
        userOrganization={userOrganization}
        googleConnectComponent={
          <GoogleConnect
            user={user}
            userOrganization={userOrganization}
            onSuccess={showSuccess}
            onError={showError}
            onWarning={showWarning}
          />
        }
      />
      <main style={styles.main}>
        <div style={styles.inner}>
          <header style={styles.welcome}>
            <div style={styles.eyebrow}>{formatToday()}</div>
            <h1 style={styles.heading}>
              {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
            </h1>
            <p style={styles.subheading}>
              Pick a workspace, or jump straight into a chat.
            </p>
          </header>

          {loading ? (
            <div style={styles.muted}>Loading your dashboards…</div>
          ) : (
            <div style={styles.grid}>
              {visible.map((entry) => (
                <DashboardCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  main: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '40px 32px 56px',
    background: 'transparent',
  },
  inner: {
    maxWidth: 1120,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
  welcome: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxWidth: 920,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    marginBottom: 2,
  },
  heading: {
    margin: 0,
    fontSize: 40,
    fontWeight: 700,
    letterSpacing: '-0.022em',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
    lineHeight: 1.1,
  },
  subheading: {
    margin: '8px 0 0',
    fontSize: 16,
    color: 'var(--fg-3)',
    lineHeight: 1.55,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 16,
  },
  muted: {
    color: 'var(--fg-4)',
    fontSize: 14,
  },
};

export default DashboardLauncher;
